import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import '../index.css';
import CustomTable, { Column } from '../components/CustomTable'; // Import the new CustomTable instead of Table
import ExpenseModal from '../components/ExpenseModal';
import { ExpenseInterface } from '../types';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import ExpenseIcon from '../assets/svgs/ExpenseIcon';
import ReactGA from 'react-ga4';
import { CreateButton } from '../components/SharedComponents';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';


const Wrapper = styled.div`
    background-color:white;
    width:100%;
    min-height:100vh;
    padding:20px 20px;
    box-sizing:border-box;
    @media (max-width: 768px) {
        padding:20px 20px;
    }
`;

export default function Expenses() {
    const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
    const [selectedExpense, setSelectedExpense] = useState<ExpenseInterface | null>(null);
    const [expenses, setExpenses] = useState<ExpenseInterface[]>([]);
    const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');

    const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));


    // Updated columns definition for CustomTable
    const columns: Column[] = [
        {
            field: 'date_created',
            headerName: 'Expense Date',
            minWidth: 150,
            headerClassName: "table-header",
            cellClassName: 'text-gray-500',
            renderCell: (params) => {
                const date = params.row.backdated_expense_date || params.row.date_created;
                return moment(date).format('YYYY-MM-DD');
            },
            valueGetter: (params) => {
                return params.row.backdated_expense_date || params.row.date_created;
            },
            flex: 1
        },
        {
            field: 'title',
            headerName: 'Item',
            minWidth: 260,
            headerClassName: "table-header",
            cellClassName: 'font-medium text-gray-900',
            flex: 2
        },
        {
            field: 'price',
            headerName: 'Price',
            minWidth: 120,
            headerClassName: "table-header",
            cellClassName: 'text-gray-500',
            valueGetter: (params) => `${currency} ${Number(params.value).toFixed(2)}`,
            flex: 1
        },
        {
            field: 'expense_type',
            headerName: 'Type',
            minWidth: 140,
            headerClassName: "table-header",
            cellClassName: 'font-medium text-gray-900',
            flex: 1
        },
        {
            field: 'date_updated',
            headerName: 'Last Updated',
            minWidth: 150,
            headerClassName: "table-header",
            cellClassName: 'text-gray-500',
            renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
            flex: 1
        },
    ];

    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: "/expenses",
            title: "Expenses",
        });

        setFetchState('loading');
        apiClient.get('/expenses').then((response: any) => {
            setFetchState('success');
            setExpenses(response.data);
            ReactGA.event({
                category: "Expenses",
                action: "Fetched Expenses",
                label: "Fetch Success",
            });
        }).catch((error) => {
            console.log("error fetching expenses", error);
            setFetchState('failed');
            ReactGA.event({
                category: "Expenses",
                action: "Fetch Expenses Failed",
                label: "Fetch Failed",
            });
        });
    }, []);

    const handleRowClick = (params: { row: ExpenseInterface }) => {
        const expense = params.row;
        ReactGA.event({
            category: "User Interaction",
            action: "Clicked Expense Row",
            label: `Expense ID: ${expense.id}`,
        });
        setSelectedExpense(expense);
        setIsEditPanelVisible(true);
    };

    const showCreateExpensePanel = () => {
        ReactGA.event({
            category: "User Interaction",
            action: "Clicked Add New Expense Button",
            label: "Add New Expense",
        });
        setIsEditPanelVisible(true);
    };

    const handleExpenseDelete = (expenseId: number) => {
        // Remove the deleted expense from the list
        setExpenses(expenses.filter((expense: ExpenseInterface) => expense.id !== expenseId));
        ReactGA.event({
            category: "Expenses",
            action: "Deleted Expense",
            label: `Expense ID: ${expenseId}`,
        });
    };

    const hideCreateExpensePanel = (expense: ExpenseInterface | null = null) => {
        const wasCreatingNew = selectedExpense === null;
        setIsEditPanelVisible(false);
        setSelectedExpense(null);

        if (expense) {
            if (wasCreatingNew) {
                // This is a new expense - add it to the list
                setExpenses([...expenses, expense]);
                ReactGA.event({
                    category: "Expenses",
                    action: "Created New Expense",
                    label: "New Expense",
                });
            } else {
                // This is an update to existing expense
                setExpenses(expenses.map((p: ExpenseInterface) => {
                    if (p.id === expense.id) {
                        return expense;
                    }
                    return p;
                }));
                ReactGA.event({
                    category: "Expenses",
                    action: "Updated Expense",
                    label: `Expense ID: ${expense.id}`,
                });
            }
        } else {
            ReactGA.event({
                category: "User Interaction",
                action: "Closed Expense Panel",
                label: "No Expense",
            });
        }
    };

    return (
        <Wrapper>
            <Header
                title="Expenses"
                description="Create edit or delete expenses here"
                headerComponents={[
                    <CreateButton key="create-button" onClick={showCreateExpensePanel}>
                        <ExpenseIcon />
                        <span>Add Expense</span>
                    </CreateButton>
                ]}
            />
            <CustomTable
                columns={columns}
                objects={expenses}
                sx={{ marginTop: '50px' }}
                onRowClick={(params) => handleRowClick(params)}
                emptyStateComponent={() => (
                    <EmptyState
                        handleButtonClick={showCreateExpensePanel}
                        title="No Expenses Logged Yet"
                        description="Track your business spending by adding expenses here. Stay on top of your finances and keep your records organized."
                        buttonText="Record An Expense"
                        icon={<ExpenseIcon width={50} height={50} />}
                    />
                )}
                fetchState={fetchState}
                initialRowsPerPage={25}
                sortConfig={{ key: 'date_created', direction: 'desc' }}
            />
            <ExpenseModal
                isVisible={isEditPanelVisible}
                handleHidePanel={hideCreateExpensePanel}
                expense={selectedExpense}
                onExpenseDelete={handleExpenseDelete}
            />
        </Wrapper>
    );
}